import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  AlertColor,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, MouseEvent, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NewBookingSendEmailComponent from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent';
import {
  appendEmailAttachmentOptions,
  getEmailAttachmentVisibility,
} from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/emailAttachmentOptions';
import { NewBookingSendEmailFormComponent } from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/SendEmailFormComponent';
import VenueDetailsPageSummary from 'screens/Authenticated/NewBooking/VenueDetailsPageSummary';
import { extractCostingErrorMessage, VENUE_DETAILS_COSTING_DEBOUNCE_MS } from 'screens/Authenticated/NewBooking/venueDetailsCostingUtils';
import { User } from 'utils/redux/reducer/authentication-slice';
import strings from '../../../../../common/Translation/Translate';
import PrimaryButton from '../../../../../components/Button/PrimaryButton';
import Form, { FormDataModel } from '../../../../../components/Form';
import Notification from '../../../../../components/Notification';
import PageLoader from '../../../../../components/PageLoader';
import axiosInstance from '../../../../../utils/axios';
import { URLS } from '../../../../../utils/constants/urls';
import Navigation from '../../../../../utils/hooks/Navigation';
import {
  ScrollTop,
  createDropdownData,
  createLayoutDropdownData,
  createPricePackageDropdownData,
  downloadExportFile,
  fetchErrorFields,
  getFileName,
  removeErrorFieldsFromValues,
} from '../../../../../utils/validators/HelperFunctions';
import { AddEditBookingRooms } from './AddEditBookingRooms/AddEditBookingRooms';
import {
  ExistingBookedRoomSummary,
  ExistingReservationGroup,
  ExternalBookedRoomPayload,
  YcsRoomBooking,
} from './AddEditBookingRooms/YcsRoomBooking';
import Section from 'components/Section/Section';
import { NewBookingAddon } from 'screens/Authenticated/NewBooking/NewBookingAddOn';
import {
  EditBookingForm,
  EditBookingOthersForm,
  EditBookingTaxForm,
  getAllEditBookingFormModels,
} from './EditBookingForm';
import { buildEditBookingCostingRequest } from './editBookingCostingUtils';
import PricingStrategySection, {
  PricingStrategyValues,
  buildHourlyRentalBreakdown,
} from 'components/PricingStrategySection';
import { hourlyPricingFlag } from 'utils/redux/reducer/booking-slice';
import Config from 'utils/Config';
import axios from 'axios';
import { identifiers } from 'utils/constants/identifiers';
import PreviewContract from 'screens/Authenticated/NewBooking/PreviewContract';
import { PreviewPopover } from 'screens/Authenticated/NewBooking/PreviewPopover';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import { determinePartOfDay } from 'utils/validators';
import { fetchFromStorage } from 'utils/storage';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';
import {
  PaymentScheduleSection,
  pickPaymentScheduleValues,
  validatePaymentSchedule,
} from 'components/PaymentScheduleSection';

const CONTRACT = identifiers.CONTRACT;
const PROPOSAL = identifiers.PROPOSAL;
const PI = identifiers.PI;
const ENQUIRY = identifiers.ENQUIRY;

export const EditBooking: FC = () => {
  let roomAvailable = true;
  const { id } = useParams() as any;
  let formRef: RefObject<Form | null | undefined> = useRef();
  let taxFormRef: RefObject<Form | null | undefined> = useRef();
  let othersFormRef: RefObject<Form | null | undefined> = useRef();
  let sendEmailFormRef: RefObject<Form | null | undefined> = useRef();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [rooms, setRooms] = useState<any>([]);
  /** YCS externalBookedRooms payload emitted by YcsRoomBooking. Replaces existing rooms on save. */
  const [externalBookedRooms, setExternalBookedRooms] = useState<ExternalBookedRoomPayload[]>([]);
  const [updatedData, setUpdatedData] = useState<any>({});
  const [isOdc, setIsOdc] = useState<boolean>(false);
  const [addonsList, setAddonsList] = useState<any>([]);
  const [bookedAddOns, setBookedAddOns] = useState<any>([]);
  const [serviceCharges, setServiceCharges] = useState<any>([]);
  const [masterDataObject, setMasterDataObject] = useState<any>({});
  const [updatedAmount, setUpdatedAmount] = useState<any>({});
  const [isComplementary, setIsComplementary] = useState<boolean>(false);
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState<boolean>(false);
  const [eventSubtypeOptions, setEventSubtypeOptions] = useState<any[]>([]);
  const [pastBookingWindowDays, setPastBookingWindowDays] = useState<number | null>(null);
  const [sendEmailHasError, setSendEmailHasError] = useState<boolean>(false);
  const [sendEmailPageResponse, setSendEmailPageResponse] = useState<any>({});
  const [previewData, setPreviewData] = useState('');
  const [closeModal, setCloseModal] = useState<string | false>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [paymentScheduleErrors, setPaymentScheduleErrors] = useState<Record<string, string>>({});
  const [costingPageResponse, setCostingPageResponse] = useState<Record<string, any>>({});
  const [costingLoading, setCostingLoading] = useState(false);
  const [costingError, setCostingError] = useState<string | null>(null);
  const costingRequestSeqRef = useRef(0);
  const costingDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ignore auto form sync (e.g. guest-count capacity) right after load so costing is not POSTed unprompted. */
  const suppressLiveCostingUntilRef = useRef(0);
  const updateReasonRef = useRef<string>('');
  /** Sync guard so a second Save click in the same tick cannot start another PUT/POST. */
  const savingRef = useRef(false);
  const [updateReasonDialog, setUpdateReasonDialog] = useState<{
    open: boolean;
    reason: string;
    error: string;
  }>({ open: false, reason: '', error: '' });
  const [pricing, setPricing] = useState<PricingStrategyValues>({
    overrideHallRental: false,
    overridePricePerPerson: false,
    overridePricePerHour: false,
    overrideMinimumRevenue: false,
    hallRental: null,
    pricePerPerson: null,
    minimumRevenue: null,
    pricePerHour: null,
  });

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const theme = useTheme();
  const [attachment, setAttachment] = useState<any>([]);
  const [deletedImage, setDeletedImage] = useState<any>([]);
  const token = fetchFromStorage(identifiers.access_token);
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const hourlyPricing = useSelector(hourlyPricingFlag);

  const hourlyRentalBreakdown = useMemo(() => {
    if (!hourlyPricing) return undefined;
    const rate =
      pricing.overridePricePerHour && pricing.pricePerHour !== '' && pricing.pricePerHour != null
        ? Number(pricing.pricePerHour)
        : Number(updatedData?.pricePerHour);
    if (!Number.isFinite(rate) || rate <= 0) return undefined;
    return buildHourlyRentalBreakdown(updatedData?.startTime, updatedData?.endTime, rate);
  }, [
    hourlyPricing,
    updatedData?.startTime,
    updatedData?.endTime,
    updatedData?.pricePerHour,
    pricing.overridePricePerHour,
    pricing.pricePerHour,
  ]);

  const loggedInUserDetails = useSelector(User);
  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);
  const integration = fetchFromStorage('integration');
  const isIdsIntegration = integration === 'IDS';
  const useYcsRooms = ((integration as string) || '').toUpperCase() === 'YCS';
  const venues = createDropdownData(masterDataObject.venues, ['id', 'name']);
  const eventTypes = createDropdownData(masterDataObject.eventTypes, ['id', 'name']);
  const pricePackages = createPricePackageDropdownData(masterDataObject.pricePackages);
  const layouts = createLayoutDropdownData(masterDataObject.layouts);
  const partOfDays = createDropdownData(masterDataObject.partOfDays, ['id', 'name']);
  const taxGroups = createDropdownData(masterDataObject.taxGroups, ['id', 'name']);
  const costingStateRef = useRef({
    updatedData,
    masterDataObject,
    pricing,
    bookedAddOns,
    hourlyPricing,
    pricePackages,
    isComplementary,
  });
  const selectedPackage = pricePackages.find(
    (p: any) => String(p.value) === String(updatedData?.pricePackage),
  );
  const selectedPackageLabel = selectedPackage?.label ?? undefined;
  /** Package catalog rate — same source New Booking uses for Pricing Configuration cards. */
  const defaultPricePerPerson =
    selectedPackage?.pricePerPerson ??
    updatedAmount?.pricePerPerson ??
    updatedAmount?.formattedPricePerPerson ??
    null;
  const defaultMinimumRevenue =
    updatedAmount?.minimumRevenue ?? updatedAmount?.formattedMinimumRevenue ?? null;
  const defaultHallRental = updatedAmount?.hallRental ?? updatedAmount?.formattedHallRental ?? null;
  const defaultPricePerHour = updatedAmount?.pricePerHour ?? updatedAmount?.formattedPricePerHour ?? null;
  const pricingPackageName =
    normalizeCustomPricePackageName(updatedData?.customPricePackageName) ||
    selectedPackageLabel ||
    updatedAmount?.pricePackageName ||
    undefined;
  const selectedVenueLabel =
    venues.find((v: any) => String(v.value) === String(updatedData?.venue))?.label ??
    masterDataObject.selectedVenue?.label ??
    undefined;
  const selectedLayoutLabel =
    layouts.find((l: any) => String(l.value) === String(updatedData?.layout))?.label ?? null;
  const selectedLayoutMaxCapacity =
    layouts.find((l: any) => String(l.value) === String(updatedData?.layout))?.maxCapacity ?? null;
  const selectedEventTypeLabel =
    eventTypes.find((e: any) => String(e.value) === String(updatedData?.eventType))?.label ??
    masterDataObject.selectedEventType?.label ??
    null;
  const headerCustomerName = String(masterDataObject.customerName ?? '').trim();

  const addonCatalog = useMemo(() => {
    if (Array.isArray(addonsList)) return addonsList;
    if (!addonsList || typeof addonsList !== 'object') return [];
    return Object.keys(addonsList)
      .map(key => addonsList[key])
      .flat();
  }, [addonsList]);

  const editBookingFormProps = {
    strings,
    pricePackages,
    venues,
    eventTypes,
    layouts,
    partOfDays,
    serviceChargeDropdown,
    serviceCharges,
    hourlyPricing,
    isOdc,
    eventSubtypeOptions,
    excessGuestsPercentage: updatedData?.excessGuestsPercentage,
    selectedPricePackage: updatedData?.pricePackage,
    taxGroups,
    layoutMaxCapacity: selectedLayoutMaxCapacity,
    layoutName: selectedLayoutLabel,
    layoutSelected: Boolean(updatedData?.layout),
    venueMaxCapacity: null as number | null,
    venueName: selectedVenueLabel,
  };

  const headerMetaItems = useMemo(() => {
    const items: { icon: ReactNode; label: string }[] = [];
    if (headerCustomerName) {
      items.push({
        icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
        label: headerCustomerName,
      });
    }
    if (selectedEventTypeLabel?.trim()) {
      items.push({
        icon: <EventOutlinedIcon sx={{ fontSize: 15 }} />,
        label: selectedEventTypeLabel.trim(),
      });
    }
    if (selectedVenueLabel?.trim()) {
      items.push({
        icon: <PlaceOutlinedIcon sx={{ fontSize: 15 }} />,
        label: selectedVenueLabel.trim(),
      });
    }
    return items;
  }, [headerCustomerName, selectedEventTypeLabel, selectedVenueLabel]);

  const pageHeaderMeta = headerMetaItems.length ? (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
      spacing={0.75}
      sx={{ rowGap: 0.25, color: 'text.secondary', minWidth: 0 }}>
      {headerMetaItems.map((item, index) => (
        <Stack
          key={`${item.label}-${index}`}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ minWidth: 0 }}>
          {index > 0 ? (
            <Typography
              component="span"
              sx={{ fontSize: '0.8rem', lineHeight: 1, mr: 0.25, opacity: 0.7 }}
              aria-hidden>
              ·
            </Typography>
          ) : null}
          <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, opacity: 0.9 }}>{item.icon}</Box>
          <Typography
            noWrap
            sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.35, color: 'text.primary' }}>
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  ) : null;

  const getInitialResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getMasterDataForLineItem(id));
      setPastBookingWindowDays(data?.pastBookingWindowDays ?? null);
      setMasterDataObject({
        ...data,
        serviceCharge: data.serviceCharge === null ? null : data.serviceCharge?.value,
      });
      // setRoomAvailable(data.roomsAvailable);
      updateDataList({
        ...data,
        serviceCharge: data.serviceCharge === null ? null : data.serviceCharge?.value,
        startTime: parseTimeValue(data.startTime) ?? data.startTime,
        endTime: parseTimeValue(data.endTime) ?? data.endTime,
      });
      setIsComplementary(data.complementary === null ? false : data.complementary);
      const a = createDropdownData(data?.serviceCharges, ['id', 'name']) || [];
      setServiceCharges(a);
      setEventSubtypeOptions(createDropdownData(data?.eventSubTypes, ['name', 'name']));
      setIsLoading(false);
      // Suppress live POST costing while form fields finish syncing after hydrate.
      suppressLiveCostingUntilRef.current = Date.now() + VENUE_DETAILS_COSTING_DEBOUNCE_MS + 500;
    } catch (error: any) {
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getInitialResponse();
    }
  }, [getInitialResponse, id]);

  const updateDataList = (data: any) => {
    getAllAmount(data.selectedPricePackage);

    const initialData = {
      ...data,
      id: data?.id,
      guaranteedGuests: data.guaranteedGuests,
      excessGuestsPercentage: data.excessGuestsPercentage ?? 0,
      holdDays: data.holdDays,
      bookingDate: data.bookingDate ?? data.booking_date ?? null,
      pricePackage: data.selectedPricePackage?.value,
      customPricePackageName: data.customPricePackageName ?? '',
      layout: data.selectedLayout?.value,
      venue: data.selectedVenue?.value,
      eventType: data.selectedEventType?.value,
      taxGroupOnGuestCharge: data.selectedTaxGroupOnGuestCharge?.value ?? '',
      taxGroupOnHallRental: data.selectedTaxGroupOnHallRental?.value ?? '',
      taxGroupOnServiceCharge: data.selectedTaxGroupOnServiceCharge?.value ?? '',
      partOfDay: data.selectedPartOfDay
        ? data.selectedPartOfDay.value
        : determinePartOfDay(formatTimeForApi(data.startTime)),
      partOfDayUserOverride: false,

      minConfirmPaymentPercentage: data.minConfirmPaymentPercentage,
      paymentSlotOnePercentage: data.paymentSlotOnePercentage,
      paymentSlotTwoPercentage: data.paymentSlotTwoPercentage,
      paymentSlotThreePercentage: data.paymentSlotThreePercentage,
      paymentSlotOneBeforeDays: data.paymentSlotOneBeforeDays,
      paymentSlotTwoBeforeDays: data.paymentSlotTwoBeforeDays,
      fullPaymentBeforeDays: data.fullPaymentBeforeDays,
      bookingConfirmationBy: data.bookingConfirmationBy,
    };

    setIsLoading(false);
    setUpdatedData(initialData);
    setIsOdc(data.isOdc);
    setAddonsList(data.addOns);
    setBookedAddOns(
      (Array.isArray(data.addOns) ? data.addOns : [])
        .filter((item: any) => item.selected)
        .map((item: any) => ({
          type: item.addOnType,
          addOnId: item.id,
          name: item.name,
          id: item.bookedAddOnId,
          maxCount: item.maxCount,
          quantity:
            item.quantity === null
              ? item.addOnType === 'PER_QUANTITY'
                ? 1
                : 0
              : item.addOnType === 'PER_QUANTITY' && item.quantity < 1
                ? 1
                : item.quantity,
          price: item.price,
        })),
    );
    setRooms(
      data.rooms.map((room: any) => ({
        ...room,
        taxGroup: room.taxGroup?.value ?? room.taxGroup ?? '',
        taxGroupName: room.taxGroup?.label ?? room.taxGroupName ?? '',
      })),
    );
    setPricing({
      ...pricing,
      overrideHallRental: data.overrideHallRental,
      overridePricePerPerson: data.overridePricePerPerson,
      overridePricePerHour: data.overridePricePerHour,
      overrideMinimumRevenue: data.overrideMinimumRevenue,
      hallRental: data.hallRental,
      pricePerPerson: data.pricePerPerson,
      pricePerHour: data.pricePerHour,
      minimumRevenue: data.minimumRevenue,
    });
  };

  const getAllAmount = async (pricePackage: any) => {
    try {
      const pricePackageId: number = pricePackage ? pricePackage.value : 0;
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getAmount(id, pricePackageId));
      if (status === 200) {
        setIsLoading(false);
        setUpdatedAmount(data);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const buildRequest = (data: any) => {
    const requestBody = {
      venue: data.venue,
      eventType: data.eventType,
      eventSubtype:
        typeof data.eventSubtype === 'object'
          ? (data.eventSubtype as any)?.label || (data.eventSubtype as any)?.value || ''
          : (data.eventSubtype as any),
      guaranteedGuests: data.guaranteedGuests,
      excessGuestsPercentage:
        data.excessGuestsPercentage === '' || data.excessGuestsPercentage == null
          ? null
          : Number(data.excessGuestsPercentage),
      pricePackageId: hourlyPricing ? 0 : data.pricePackage,
      customPricePackageName:
        !hourlyPricing && isCustomizePackage(data.pricePackage, pricePackages)
          ? normalizeCustomPricePackageName(data.customPricePackageName)
          : null,
      /*
       * `updateReason` no longer lives in the form – it comes from the
       * confirmation dialog captured just before the save is triggered.
       */
      updateReason: updateReasonRef.current || data.updateReason || '',
      additionalComments: data.additionalComments,
      holdDays: data.holdDays,
      layout: data.layout,
      placeOfEvent: data.placeOfEvent,
      addressOfEvent: data.addressOfEvent,
      taxGroupOnGuestChargeId:
        (data.taxGroupOnGuestCharge as any)?.value ?? data.taxGroupOnGuestCharge ?? null,
      taxGroupOnHallRentalId:
        (data.taxGroupOnHallRental as any)?.value ?? data.taxGroupOnHallRental ?? null,
      taxGroupOnServiceChargeId: (() => {
        const id = (data.taxGroupOnServiceCharge as any)?.value ?? data.taxGroupOnServiceCharge ?? null;
        return id === '' || id == null ? null : id;
      })(),
      startTime: formatTimeForApi(data.startTime),
      endTime: formatTimeForApi(data.endTime),
      preLeadTime:
        data.preLeadTime === '' || data.preLeadTime == null ? null : Number(data.preLeadTime),
      postLeadTime:
        data.postLeadTime === '' || data.postLeadTime == null ? null : Number(data.postLeadTime),
      partOfDay: data.partOfDay,
      serviceCharge: data.serviceCharge,
      serviceChargePercentage: data.serviceChargePercentage,
      // Payment details
      minConfirmPaymentPercentage: data.minConfirmPaymentPercentage,
      paymentSlotOnePercentage: data.paymentSlotOnePercentage,
      paymentSlotTwoPercentage: data.paymentSlotTwoPercentage,
      paymentSlotThreePercentage: data.paymentSlotThreePercentage,
      paymentSlotOneBeforeDays: data.paymentSlotOneBeforeDays,
      paymentSlotTwoBeforeDays: data.paymentSlotTwoBeforeDays,
      fullPaymentBeforeDays: data.fullPaymentBeforeDays,
      bookingConfirmationBy: data.bookingConfirmationBy,
      bookedRooms: useYcsRooms
        ? []
        : rooms.map((data: any) => {
            const { taxCodes: _legacyTaxCodes, ...room } = data;
            return {
              ...room,
              price: data.pricePerDay,
              taxGroupId: data.taxGroup?.value ?? data.taxGroup ?? null,
            };
          }),
      externalBookedRooms: useYcsRooms ? externalBookedRooms : [],
      bookedAddOns: bookedAddOns,
      overrideHallRental: pricing.overrideHallRental,
      overridePricePerPerson: pricing.overridePricePerPerson,
      overridePricePerHour: !!pricing.overridePricePerHour,
      overrideMinimumRevenue: pricing.overrideMinimumRevenue,
      hallRental: pricing.hallRental || 0,
      pricePerPerson: pricing.pricePerPerson || 0,
      pricePerHour: pricing.pricePerHour || 0,
      minimumRevenue: pricing.minimumRevenue || 0,
      complementary: isComplementary,
    };
    return requestBody;
  };
  const handlePaymentScheduleChange = useCallback((field: string, value: unknown) => {
    setUpdatedData((prev: any) => ({ ...prev, [field]: value }));
    setPaymentScheduleErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFormValidationHelper = () => {
    const mainRef = formRef.current as { getFormData: () => { formData: FormDataModel; isFormValid: boolean } };
    const taxRef = taxFormRef.current as
      | { getFormData: () => { formData: FormDataModel; isFormValid: boolean } }
      | null
      | undefined;
    const othersRef = othersFormRef.current as
      | { getFormData: () => { formData: FormDataModel; isFormValid: boolean } }
      | null
      | undefined;

    const { formData: mainData, isFormValid: isMainValid } = mainRef.getFormData();
    const { formData: taxData, isFormValid: isTaxValid } = taxRef?.getFormData
      ? taxRef.getFormData()
      : { formData: {} as FormDataModel, isFormValid: true };
    const { formData: othersData, isFormValid: isOthersValid } = othersRef?.getFormData
      ? othersRef.getFormData()
      : { formData: {} as FormDataModel, isFormValid: true };

    const paymentValues = pickPaymentScheduleValues(updatedData);
    const paymentValidation = validatePaymentSchedule(paymentValues);
    const mergedData = {
      ...(mainData as any),
      ...(taxData as any),
      ...(othersData as any),
      ...paymentValues,
    } as any;
    const data = removeErrorFieldsFromValues(mergedData);
    setHasError(false);

    if (isMainValid && isTaxValid && isOthersValid && paymentValidation.valid) {
      setPaymentScheduleErrors({});
      let bookedAddOnsNumberError = [] as any;
      let bookedAddOnsPriceError = [] as any;
      let bookedAddOnsQuantityLimitError = [] as any;
      bookedAddOns.forEach((item: any) => {
        if (item.type === 'PER_QUANTITY' && !item.quantity) item = { ...item, quantity: 0 };
        if (item.type === 'PER_QUANTITY' && item.quantity < 1) {
          bookedAddOnsNumberError.push(item.name);
        } else if (item.type === 'PER_QUANTITY' && item.quantity > 100000) {
          bookedAddOnsQuantityLimitError.push(item.name);
        } else if (item.price < 0) {
          bookedAddOnsPriceError.push(item.name);
        }
      });

      if (bookedAddOnsQuantityLimitError.length > 0) {
        ScrollTop(0);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsQuantityLimitError.join(', ')} ${strings.addOn_QuantityLimitError}`,
        });
        return false;
      }
      if (bookedAddOnsNumberError.length > 0) {
        ScrollTop(0);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsNumberError.join(', ')} ${strings.addOn_Error}`,
        });
        return false;
      }
      if (bookedAddOnsPriceError.length > 0) {
        ScrollTop(0);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsPriceError.join(', ')} ${strings.addOnPriceError}`,
        });
        return false;
      }

      const startMoment = parseTimeValue(data.startTime as any);
      const endMoment = parseTimeValue(data.endTime as any);
      if (startMoment && endMoment && (startMoment.isSame(endMoment) || startMoment.isAfter(endMoment))) {
        setMessage({ display: true, severity: 'error', message: strings.timePickerValidation });
        return false;
      }
      return buildRequest(data);
    }

    setPaymentScheduleErrors(paymentValidation.errors);
    setHasError(true);
    setMessage({
      display: true,
      severity: 'error',
      message: `${strings.required_error_prefix_message} ${fetchErrorFields(mergedData, [
        ...getAllEditBookingFormModels({
          ...editBookingFormProps,
          selectedPricePackage: mergedData.pricePackage,
        }),
      ]).join(', ')}`,
    });
    setHasError(true);
    setIsLoading(false);
    return false;
  };

  const handleEditFormChange = (field: string, value: any, formData: FormDataModel) => {
    setUpdatedData((prev: any) => ({
      ...prev,
      [field]: value,
      ...(field === 'pricePackage' && !isCustomizePackage(value, pricePackages) ? { customPricePackageName: '' } : {}),
    }));
    scheduleEditBookingCostingRefresh();

    if (field === 'pricePackage') {
      getAllAmount({ value });
    }

    if (field === 'venue') {
      const eventTypeId = formData.eventType ?? updatedData?.eventType;
      if (eventTypeId && value) {
        axiosInstance
          .get(URLS.getDropdownsForVenueConfig(String(eventTypeId), String(value), 'false'))
          .then(({ data }) =>
            setUpdatedData((prev: any) => ({
              ...prev,
              excessGuestsPercentage: Number(data?.excessGuestsPercentage) || 0,
            })),
          )
          .catch(() =>
            setUpdatedData((prev: any) => ({
              ...prev,
              excessGuestsPercentage: 0,
            })),
          );
      } else {
        setUpdatedData((prev: any) => ({
          ...prev,
          excessGuestsPercentage: 0,
        }));
      }
    }

    if (field === 'partOfDay') {
      setUpdatedData((prev: any) => ({ ...prev, partOfDayUserOverride: true }));
      return;
    }
  };

  const refreshEditBookingCosting = useCallback(async () => {
    const {
      updatedData: data,
      masterDataObject: master,
      pricing: pricingValues,
      bookedAddOns: addOns,
      hourlyPricing: isHourly,
      pricePackages: packages,
      isComplementary: complementary,
    } = costingStateRef.current;

    if (!data?.venue) {
      setCostingPageResponse({});
      setCostingError(null);
      return;
    }
    const requestBody = buildEditBookingCostingRequest({
      updatedData: data,
      masterDataObject: master,
      pricing: pricingValues,
      bookedAddOns: addOns,
      hourlyPricing: isHourly,
      pricePackages: packages,
      isComplementary: complementary,
      excludeBookingLineItemId: id,
    });
    if (!requestBody) {
      setCostingPageResponse({});
      setCostingError(null);
      return;
    }

    const requestSeq = ++costingRequestSeqRef.current;
    setCostingLoading(true);
    try {
      const { status, data: responseData } = await axiosInstance.post(URLS.calculateBill, requestBody);
      if (requestSeq !== costingRequestSeqRef.current) return;
      if (status === 200 || status === 201) {
        setCostingPageResponse(responseData ?? {});
        setCostingError(null);
      }
    } catch (error: any) {
      if (requestSeq !== costingRequestSeqRef.current) return;
      setCostingPageResponse({});
      const message = extractCostingErrorMessage(error, strings.new_booking_event_summary_error);
      setCostingError(message);
      setMessage({
        display: true,
        severity: 'error',
        message,
      });
    } finally {
      if (requestSeq === costingRequestSeqRef.current) {
        setCostingLoading(false);
      }
    }
  }, [id]);

  const scheduleEditBookingCostingRefresh = useCallback(() => {
    if (Date.now() < suppressLiveCostingUntilRef.current) return;
    if (costingDebounceTimerRef.current) {
      clearTimeout(costingDebounceTimerRef.current);
    }
    costingDebounceTimerRef.current = setTimeout(() => {
      costingDebounceTimerRef.current = null;
      void refreshEditBookingCosting();
    }, VENUE_DETAILS_COSTING_DEBOUNCE_MS);
  }, [refreshEditBookingCosting]);

  const handlePricingChange = useCallback(
    (next: PricingStrategyValues) => {
      setPricing(next);
      scheduleEditBookingCostingRefresh();
    },
    [scheduleEditBookingCostingRefresh],
  );

  const handleBookedAddOnsChange = useCallback(
    (value: any) => {
      setBookedAddOns(value);
      scheduleEditBookingCostingRefresh();
    },
    [scheduleEditBookingCostingRefresh],
  );

  useEffect(() => {
    costingStateRef.current = {
      updatedData,
      masterDataObject,
      pricing,
      bookedAddOns,
      hourlyPricing,
      pricePackages,
      isComplementary,
    };
  }, [
    updatedData,
    masterDataObject,
    pricing,
    bookedAddOns,
    hourlyPricing,
    pricePackages,
    isComplementary,
  ]);

  /** Saved billing summary on load — no live POST until the user edits. */
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setCostingLoading(true);
      try {
        const { data } = await axiosInstance.get(URLS.getBillingCostingSummary(id));
        if (!cancelled) {
          setCostingPageResponse(data ?? {});
          setCostingError(null);
        }
      } catch {
        if (!cancelled) {
          setCostingPageResponse({});
        }
      } finally {
        if (!cancelled) {
          setCostingLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(
    () => () => {
      if (costingDebounceTimerRef.current) {
        clearTimeout(costingDebounceTimerRef.current);
      }
    },
    [],
  );

  const emailAttachmentVisibility = getEmailAttachmentVisibility({
    isContractTemplatePresent: masterDataObject.isContractTemplatePresent,
    isProposalTemplatePresent: masterDataObject.isProposalTemplatePresent,
    isPiTemplatePresent: masterDataObject.isPiTemplatePresent,
    isEnquiryTemplatePresent: masterDataObject.isEnquiryTemplatePresent,
    isEnquiryStatus: masterDataObject.status === 'ENQUIRY',
  });

  const handleSave = async (e: MouseEvent<HTMLButtonElement>, submitFromDrawer: boolean) => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    try {
      e.preventDefault();
      const requestBody = handleFormValidationHelper();
      if (requestBody === false) {
        return;
      } else {
        if (submitFromDrawer) {
            const { getFormData } = sendEmailFormRef.current as {
              getFormData: () => { formData: FormDataModel; isFormValid: boolean };
            };
            const { formData, isFormValid } = getFormData();
            setSendEmailHasError(false);
            if (isFormValid) {
              const emailFormData = removeErrorFieldsFromValues(formData);
              setIsLoading(true);

              await axiosInstance.put(URLS.editBookings(id), requestBody);

              const myFormData = new FormData();
              attachment.forEach((file: any) => {
                myFormData.append('files', file);
              });
              if (attachment.length === 0) {
                myFormData.append('files', [] as any);
              }
              myFormData.append('deletedFiles', deletedImage);
              myFormData.append('to', emailFormData.to);
              myFormData.append('cc', emailFormData.cc);
              myFormData.append('bcc', emailFormData.bcc);
              myFormData.append('type', 'EDIT_BOOKING');
              appendEmailAttachmentOptions(myFormData, emailAttachmentVisibility);

              const config = {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              };
              const { status } = await axiosInstance.post(URLS.sendBookingEmailAttachment(id), myFormData, config);
              if (status === 200 || status === 201) {
                setDeletedImage('');
                setSendEmailPageResponse({});
                setAttachment([]);
                setOpenSendEmailDrawer(false);
                setIsLoading(false);
                getAllAmount({ value: requestBody?.pricePackageId });
                getInitialResponse();
                setMessage({
                  display: true,
                  severity: 'success',
                  message: strings.common_save_message,
                });
                return true;
              }
            } else {
              setMessage({
                display: true,
                severity: 'error',
                message: `${strings.required_error_prefix_message} ${fetchErrorFields(
                  formData,
                  NewBookingSendEmailFormComponent(strings),
                ).join(', ')}`,
              });
              setSendEmailHasError(true);
              setIsLoading(false);
            }
          } else {
            const arr =
              masterDataObject.email === ''
                ? [loggedInUserDetails.email]
                : [loggedInUserDetails.email, masterDataObject.email];
            setSendEmailPageResponse({ to: arr.join(',') });
            setOpenSendEmailDrawer(true);
          }
      }
    } catch (error: any) {
      setIsLoading(false);
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      savingRef.current = false;
    }
  };

  const openUpdateReasonDialog = () => {
    setUpdateReasonDialog({ open: true, reason: updateReasonRef.current, error: '' });
  };

  const closeUpdateReasonDialog = () => {
    setUpdateReasonDialog(prev => ({ ...prev, open: false, error: '' }));
  };

  const confirmUpdateReason = async (e: MouseEvent<HTMLButtonElement>) => {
    const reason = (updateReasonDialog.reason || '').trim();
    if (!reason) {
      setUpdateReasonDialog(prev => ({ ...prev, error: strings.edit_booking_update_reason + ' is required' }));
      return;
    }
    updateReasonRef.current = reason;
    setUpdateReasonDialog({ open: false, reason, error: '' });
    await handleSave(e, false);
  };

  const handleCancelBookedRoom = async (group: ExistingReservationGroup) => {
    const anchorRoom = group.rooms[0];
    if (!anchorRoom) {
      return;
    }
    const bookedRoomIds = new Set(group.rooms.map((room: ExistingBookedRoomSummary) => room.bookedRoomId));
    try {
      setIsLoading(true);
      await axiosInstance.delete(URLS.cancelBookedRoom(anchorRoom.bookedRoomId));
      // Drop every row that shares this YCS reservation (backend removes the full group).
      setRooms((prev: any[]) => (prev || []).filter((r: any) => !bookedRoomIds.has(r?.bookedRoomId)));
      await getInitialResponse();
      setMessage({
        display: true,
        severity: 'success',
        message:
          group.isSharedYcsReservation && group.externalReservationNo && group.externalProvider
            ? `Reservation ${group.externalReservationNo} cancelled on ${group.externalProvider} (${
                group.rooms.length
              } room${group.rooms.length === 1 ? '' : 's'} removed).`
            : 'Room removed from booking.',
      });
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error_description ||
          error?.message ||
          'Failed to cancel reservation.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFileDetails = (
    value: string,
    field: string,
    error?: { error: boolean; message: string },
    isDeleted?: any,
  ) => {
    const deletedArray = [...deletedImage];
    if (Array.isArray(isDeleted)) {
      deletedArray.push(isDeleted[0]?.id);
      setDeletedImage(deletedArray);
      setAttachment(value);
    } else {
      setAttachment(value);
    }
  };
  const handlePreviewContract = async (type: any) => {
    try {
      const body = handleFormValidationHelper();
      if (body === false) return;
      setIsLoading(true);
      const { status, data } = await axiosInstance.post(
        `${URLS.bookingPreviewTemplateByIdForEditBooking(id)}?type=${type}`,
        body,
      );
      if (status === 200) {
        setIsLoading(false);
        setCloseModal(type);
        setPreviewData(data);
      }
    } catch (e: any) {
      setIsLoading(false);
      if (e.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: e.response?.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: e.message,
        });
      }
    }
  };

  const downloadContract = async (type: any) => {
    const data = handleFormValidationHelper();
    if (data === false) return;
    setIsLoading(true);
    axios({
      url: `${Config.BaseURL + URLS.bookingPreviewTemplatePdfByIdForEditBooking(id)}?type=${type}`,
      method: 'POST',
      data: data,
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
      .then(response => {
        setIsLoading(false);
        downloadExportFile(response, 'pdf', getFileName(type));
      })
      .catch(err => {
        setIsLoading(false);
        if (err?.response?.status === 403) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.forbidden,
          });
        }
      });
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <Box className="content">
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}

        <Box sx={{ display: activeTab === 0 || !roomAvailable ? 'block' : 'none' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1, md: 1.25 },
              alignItems: 'stretch',
              // Keep Event Summary in view: only the form column scrolls (header scrolls with it).
              height: { md: 'calc(100vh - 100px)' },
              minHeight: { md: 480 },
            }}>
            <Box
              sx={{
                width: { xs: '100%', md: '74%' },
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowY: { md: 'auto' },
                pr: { md: 0.5 },
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minHeight: 40,
                  mb: pageHeaderMeta ? 0.5 : 1,
                }}>
                {roomAvailable ? (
                  <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: 36,
                      flex: 1,
                      minWidth: 0,
                      '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1.25, textTransform: 'none' },
                    }}>
                    <Tab label="Booking details" />
                    <Tab label={useYcsRooms ? 'YCS room search' : 'Rooms'} />
                  </Tabs>
                ) : (
                  <Box sx={{ flex: 1, minWidth: 0 }} />
                )}
                <Navigation url={-1} />
              </Box>
              {pageHeaderMeta ? <Box sx={{ mb: 1.25 }}>{pageHeaderMeta}</Box> : null}

              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  pt: { xs: 1, md: 1.15 },
                  px: { xs: 1.25, md: 1.75 },
                  pb: { xs: 1.25, md: 1.75 },
                }}>
                <Grid container spacing={2} sx={{ my: 0 }}>
                  <Form
                    hasError={hasError}
                    ref={formRef as RefObject<Form>}
                    model={EditBookingForm(editBookingFormProps)}
                    onChange={handleEditFormChange as any}
                    values={updatedData}
                  />
                </Grid>

                <PricingStrategySection
                  values={pricing}
                  defaults={{
                    pricePerPerson: defaultPricePerPerson,
                    minimumRevenue: defaultMinimumRevenue,
                    hallRental: defaultHallRental,
                    pricePerHour: defaultPricePerHour,
                    hourlyRentalBreakdown,
                    packageName: pricingPackageName,
                    venueName: selectedVenueLabel,
                  }}
                  onChange={handlePricingChange}
                  restrictPriceOverride={masterDataObject.restrictPriceOverride}
                  hourlyPricing={hourlyPricing}
                />

                <Grid container spacing={2} sx={{ my: 0 }}>
                  <Form
                    dropdownData={{
                      taxGroupOnGuestCharge: createDropdownData(masterDataObject.taxGroups, ['id', 'name']),
                      taxGroupOnHallRental: createDropdownData(masterDataObject.taxGroups, ['id', 'name']),
                    }}
                    hasError={hasError}
                    ref={taxFormRef as RefObject<Form>}
                    model={EditBookingTaxForm({ strings, taxGroups })}
                    onChange={handleEditFormChange as any}
                    values={updatedData}
                  />
                </Grid>

                {addonCatalog.length > 0 ? (
                  <NewBookingAddon
                    addonsDropdown={addonCatalog}
                    bookedAddOns={bookedAddOns}
                    setBookedAddOns={handleBookedAddOnsChange}
                  />
                ) : null}

                <Grid container spacing={2} sx={{ my: 0 }}>
                  <Form
                    hasError={hasError}
                    ref={othersFormRef as RefObject<Form>}
                    model={EditBookingOthersForm({
                      strings,
                      isOdc,
                      holdReleaseBaseDate:
                        String(masterDataObject?.status || '').toUpperCase() === 'TENTATIVE'
                          ? masterDataObject?.bookingDate ||
                            masterDataObject?.booking_date ||
                            updatedData?.bookingDate ||
                            null
                          : null,
                    })}
                    onChange={handleEditFormChange as any}
                    values={updatedData}
                  />
                </Grid>

                <PaymentScheduleSection
                  values={updatedData}
                  onChange={handlePaymentScheduleChange}
                  hasError={hasError}
                  errors={paymentScheduleErrors}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="complementary"
                      checked={isComplementary}
                      onChange={() => {
                        setIsComplementary(prev => !prev);
                        scheduleEditBookingCostingRefresh();
                      }}
                    />
                  }
                  label={strings.markComplementary}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.75,
                    mt: 2,
                  }}>
                  {(masterDataObject.isContractTemplatePresent ||
                    masterDataObject.isProposalTemplatePresent ||
                    masterDataObject.isPiTemplatePresent ||
                    masterDataObject.isEnquiryTemplatePresent) && (
                    <PreviewPopover
                      toolbarStyle
                      isContractTemplatePresent={masterDataObject.isContractTemplatePresent}
                      isProposalTemplatePresent={masterDataObject.isProposalTemplatePresent}
                      isPiTemplatePresent={masterDataObject.isPiTemplatePresent}
                      isEnquiryTemplatePresent={
                        masterDataObject.status === 'ENQUIRY' && masterDataObject.isEnquiryTemplatePresent
                      }
                      downloadContract={downloadContract}
                      handlePreviewContract={handlePreviewContract}
                    />
                  )}
                  <PrimaryButton
                    fullWidth={false}
                    variant="contained"
                    disabled={isLoading}
                    onClick={openUpdateReasonDialog}
                    sx={{
                      width: 'auto',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 32,
                      height: 32,
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      textTransform: 'none',
                      boxShadow: 'none',
                    }}>
                    {strings.common_button_update}
                  </PrimaryButton>
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                width: { xs: '100%', md: '26%' },
                flexShrink: 0,
                minHeight: { xs: 240, md: 0 },
                maxHeight: { xs: 320, md: 'none' },
                overflowY: 'auto',
                alignSelf: 'stretch',
              }}>
              <VenueDetailsPageSummary
                pageResponse={costingPageResponse}
                loading={costingLoading}
                error={costingError}
              />
            </Box>
          </Box>
        </Box>

        {roomAvailable && (
          <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                minHeight: 40,
                mb: pageHeaderMeta ? 0.5 : 1,
              }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  flex: 1,
                  minWidth: 0,
                  '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1.25, textTransform: 'none' },
                }}>
                <Tab label="Booking details" />
                <Tab label={useYcsRooms ? 'YCS room search' : 'Rooms'} />
              </Tabs>
              <Navigation url={-1} />
            </Box>
            {pageHeaderMeta ? <Box sx={{ mb: 1.25 }}>{pageHeaderMeta}</Box> : null}
            <Section label={strings.bookingDetails_booking_rooms} field={''} />
            {useYcsRooms ? (
              <YcsRoomBooking
                eventStartDate={updatedData.startDate}
                eventEndDate={updatedData.endDate}
                onPayloadChange={setExternalBookedRooms}
                existingBookedRooms={rooms as ExistingBookedRoomSummary[]}
                onCancelExistingBookedRoom={handleCancelBookedRoom}
              />
            ) : (
              <AddEditBookingRooms
                rooms={rooms}
                setRooms={setRooms}
                pastBookingWindowDays={pastBookingWindowDays}
                taxGroups={taxGroups}
              />
            )}
          </Box>
        )}
      </Box>
      {closeModal === CONTRACT && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_contract}
            downloadButtonText={strings.bookings_button_download_contract}
            downloadContract={downloadContract}
            pdfUrl={previewData}
            handleClose={() => {
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}
      {closeModal === PROPOSAL && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_proposal}
            downloadButtonText={strings.bookings_button_download_proposal}
            downloadContract={downloadContract}
            pdfUrl={previewData}
            handleClose={() => {
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}
      {closeModal === ENQUIRY && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_enquiry}
            downloadButtonText={strings.bookings_button_download_enquiry}
            downloadContract={downloadContract}
            pdfUrl={previewData}
            handleClose={() => {
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}
      {closeModal === PI && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_pi}
            downloadButtonText={strings.bookings_button_download_pi}
            downloadContract={downloadContract}
            pdfUrl={previewData}
            handleClose={() => {
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}

      <NewBookingSendEmailComponent
        submitButtonText={strings.common_button_update}
        openDrawer={openSendEmailDrawer}
        formRef={sendEmailFormRef}
        handleCloseDrawer={() => setOpenSendEmailDrawer(false)}
        hasError={sendEmailHasError}
        pageResponse={sendEmailPageResponse}
        handleSave={(e: any) => handleSave(e, true)}
        attachment={attachment}
        deletedImage={deletedImage}
        getFileDetails={getFileDetails}
        {...emailAttachmentVisibility}
        disabled={isLoading}
      />

      <Dialog
        open={updateReasonDialog.open}
        onClose={closeUpdateReasonDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="edit-booking-update-reason-dialog-title">
        <DialogTitle id="edit-booking-update-reason-dialog-title">{strings.edit_booking_update_reason}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a brief reason for updating this booking. This note is stored against the booking history for
            auditing.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label={strings.edit_booking_update_reason}
            value={updateReasonDialog.reason}
            onChange={e => setUpdateReasonDialog(prev => ({ ...prev, reason: e.target.value, error: '' }))}
            error={!!updateReasonDialog.error}
            helperText={updateReasonDialog.error}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeUpdateReasonDialog} variant="outlined" disabled={isLoading}>
            {strings.cancelText}
          </Button>
          <PrimaryButton variant="contained" disabled={isLoading} onClick={confirmUpdateReason}>
            {strings.common_button_update}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
